import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

export type InputType = 'text' | 'password' | 'number' | 'email';
export type InputSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'om-input',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './input.html',
  styleUrl: './input.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Input {
  control = input.required<FormControl>();
  type = input<InputType>('text');
  placeholder = input<string>('');
  inputId = input<string>('');
  size = input<InputSize>('md');
  icon = input<string>('');
  iconPosition = input<'left' | 'right'>('left');
  iconColor = input<'primary' | 'secondary' | 'slate' | 'default'>('default');
  iconSize = input<'sm' | 'md' | 'lg'>('md');

  /** For type="password" only: toggles show/hide text. */
  passwordVisible = signal(false);

  inputClasses = computed(() => {
    const sizeMap: Record<InputSize, string> = {
      sm: 'h-10',
      md: 'h-[52px]',
      lg: 'h-14',
    };
    const h = sizeMap[this.size()];
    const base = 'block w-full rounded-xl border border-white/60 dark:border-white/[0.12] bg-white/70 dark:bg-white/[0.07] backdrop-blur-sm text-slate-900 dark:text-cream-100 placeholder:text-slate-400 focus:outline-none focus:border-primary/70 focus:ring-1 focus:ring-primary/30 sm:text-sm shadow-sm transition-all duration-200';
    
    let paddingClass = 'px-4';
    if (this.type() === 'password') {
      paddingClass = 'pl-4 pr-12';
    } else if (this.icon()) {
      paddingClass = this.iconPosition() === 'left' ? 'pl-11 pr-4' : 'pl-4 pr-11';
    }

    return `${base} ${paddingClass} ${h}`;
  });

  iconClasses = computed(() => {
    const sizeMap: Record<string, string> = {
      sm: 'text-lg',
      md: 'text-xl',
      lg: 'text-2xl',
    };
    const s = sizeMap[this.iconSize()] || 'text-xl';
    
    const colorMap: Record<string, string> = {
      primary: 'text-primary',
      secondary: 'text-secondary',
      slate: 'text-slate-500 dark:text-slate-400',
      default: 'text-slate-500 dark:text-slate-400',
    };
    const c = colorMap[this.iconColor()] || colorMap['default'];
    
    return `${s} ${c}`;
  });

  togglePasswordVisibility(): void {
    this.passwordVisible.update((v) => !v);
  }
}
